import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import baseURL from '../../../baseURL';
import MasterPageShell, {
  masterPaperSx,
  masterListPaperSx,
  masterPrimaryBtnSx,
  masterFieldSx,
  masterTableHeadCellSx,
} from '../../common/MasterPageShell';

const FinancialYearManagement = () => {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [fyPage, setFyPage] = useState(0);
  const [fyRowsPerPage, setFyRowsPerPage] = useState(50);
  const [fyTotalDocs, setFyTotalDocs] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);

  const [formData, setFormData] = useState({
    yearName: '',
    startDate: '',
    endDate: '',
    description: '',
    isCurrent: false
  });

  const fetchFinancialYearsPage = async (page = 0, rowsPerPage = 50) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = `${baseURL}/master/financial-years?page=${page + 1}&limit=${rowsPerPage}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setYears(data.data || []);
        setFyTotalDocs(data.totalDocs ?? (data.data || []).length);
        setFyPage(page);
        setFyRowsPerPage(rowsPerPage);
      } else {
        setYears([]);
      }
    } catch (error) {
      console.error('Error fetching financial years:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching financial years',
        severity: 'error'
      });
      setYears([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFinancialYearsPage(0, fyRowsPerPage);
  }, []);

  const handleFyPageChange = (event, newPage) => {
    fetchFinancialYearsPage(newPage, fyRowsPerPage);
  };

  const handleFyRowsPerPageChange = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setFyRowsPerPage(newRows);
    fetchFinancialYearsPage(0, newRows);
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-calculate end date if start date changes and year name is set
    if (field === 'startDate' && value && formData.yearName) {
      const start = new Date(value);
      const end = new Date(start);
      end.setFullYear(end.getFullYear() + 1);
      end.setDate(end.getDate() - 1); // One day before next year
      setFormData(prev => ({
        ...prev,
        endDate: end.toISOString().split('T')[0]
      }));
    }
  };

  const handleYearNameChange = (event) => {
    const yearName = event.target.value;
    setFormData(prev => ({
      ...prev,
      yearName: yearName
    }));

    // Auto-calculate dates if year name is in format "2024-25"
    if (yearName.match(/^\d{4}-\d{2}$/)) {
      const [startYear] = yearName.split('-');
      const startDate = `${startYear}-04-01`;
      const endDate = `${parseInt(startYear) + 1}-03-31`;
      
      setFormData(prev => ({
        ...prev,
        startDate: startDate,
        endDate: endDate
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.yearName || !formData.startDate || !formData.endDate) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    try {
      setSaveLoading(true);
      const token = localStorage.getItem('token');
      const url = selectedYear 
        ? `${baseURL}/master/financial-years/${selectedYear._id}`
        : `${baseURL}/master/financial-years`;
      
      const method = selectedYear ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setSnackbar({
          open: true,
          message: selectedYear ? 'Financial year updated successfully' : 'Financial year created successfully',
          severity: 'success'
        });
        resetForm();
        fetchFinancialYearsPage(fyPage, fyRowsPerPage);
      } else {
        setSnackbar({
          open: true,
          message: data.message || 'Error saving financial year',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error saving financial year:', error);
      setSnackbar({
        open: true,
        message: 'Error saving financial year',
        severity: 'error'
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      yearName: '',
      startDate: '',
      endDate: '',
      description: '',
      isCurrent: false
    });
    setSelectedYear(null);
    setEditDialogOpen(false);
  };

  const handleEdit = (year) => {
    setSelectedYear(year);
    setFormData({
      yearName: year.yearName,
      startDate: new Date(year.startDate).toISOString().split('T')[0],
      endDate: new Date(year.endDate).toISOString().split('T')[0],
      description: year.description || '',
      isCurrent: year.isCurrent || false
    });
    setEditDialogOpen(true);
  };

  const handleDelete = (year) => {
    setSelectedYear(year);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedYear) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/master/financial-years/${selectedYear._id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setSnackbar({
          open: true,
          message: 'Financial year deleted successfully',
          severity: 'success'
        });
        fetchFinancialYearsPage(fyPage, fyRowsPerPage);
      } else {
        setSnackbar({
          open: true,
          message: data.message || 'Error deleting financial year',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting financial year:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting financial year',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedYear(null);
    }
  };

  const handleSetCurrent = async (year) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/master/financial-years/${year._id}/set-current`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setSnackbar({
          open: true,
          message: 'Financial year set as current',
          severity: 'success'
        });
        fetchFinancialYearsPage(fyPage, fyRowsPerPage);
      } else {
        setSnackbar({
          open: true,
          message: data.message || 'Error setting current financial year',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error setting current financial year:', error);
      setSnackbar({
        open: true,
        message: 'Error setting current financial year',
        severity: 'error'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  return (
    <MasterPageShell title="Financial Years" subtitle="Master · Calendar">
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 750, color: '#0f172a' }}>
        Manage financial year periods
      </Typography>

      <Grid container spacing={3}>
        {/* Create/Edit Form */}
        <Grid item xs={12} md={4}>
          <Card sx={masterPaperSx} elevation={0}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 750 }}>
                {selectedYear ? 'Edit Financial Year' : 'Create New Financial Year'}
              </Typography>
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Year Name (e.g., 2024-25)"
                  value={formData.yearName}
                  onChange={handleYearNameChange}
                  margin="normal"
                  required
                  helperText="Format: YYYY-YY (e.g., 2024-25). Dates will auto-calculate."
                />
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={formData.startDate}
                  onChange={handleInputChange('startDate')}
                  margin="normal"
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={formData.endDate}
                  onChange={handleInputChange('endDate')}
                  margin="normal"
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  fullWidth
                  label="Description (Optional)"
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  margin="normal"
                  multiline
                  rows={2}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Set as Current Year</InputLabel>
                  <Select
                    value={formData.isCurrent ? 'yes' : 'no'}
                    onChange={(e) => setFormData(prev => ({ ...prev, isCurrent: e.target.value === 'yes' }))}
                  >
                    <MenuItem value="no">No</MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={saveLoading}
                    fullWidth
                  >
                    {saveLoading ? <CircularProgress size={24} /> : (selectedYear ? 'Update' : 'Create')}
                  </Button>
                  {selectedYear && (
                    <Button
                      variant="outlined"
                      onClick={resetForm}
                      fullWidth
                    >
                      Cancel
                    </Button>
                  )}
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* List of Financial Years */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Existing Financial Years
              </Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <th>Year Name</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Current</th>
                        <th>Actions</th>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {years.length > 0 ? (
                        years.map((year) => (
                          <TableRow key={year._id}>
                            <TableCell>{year.yearName}</TableCell>
                            <TableCell>{formatDate(year.startDate)}</TableCell>
                            <TableCell>{formatDate(year.endDate)}</TableCell>
                            <TableCell>
                              {year.isCurrent ? (
                                <CheckCircleIcon color="success" />
                              ) : (
                                <Button
                                  size="small"
                                  onClick={() => handleSetCurrent(year)}
                                >
                                  Set Current
                                </Button>
                              )}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(year)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(year)}
                                color="error"
                                disabled={year.isCurrent}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No financial years found. Create one to get started.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={fyTotalDocs}
                    page={fyPage}
                    onPageChange={handleFyPageChange}
                    rowsPerPage={fyRowsPerPage}
                    onRowsPerPageChange={handleFyRowsPerPageChange}
                    rowsPerPageOptions={[25, 50, 100, 200]}
                    labelRowsPerPage="Rows per page:"
                  />
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete financial year "{selectedYear?.yearName}"?
            {selectedYear?.isCurrent && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This is the current financial year. You cannot delete it. Set another year as current first.
              </Alert>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={selectedYear?.isCurrent}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MasterPageShell>
  );
};

export default FinancialYearManagement;


